import { usePeekedWorkItemStore } from '@/stores/workItemStoreSlot';
// T-0892 (a): the bar is in the launch entry; the list's query is a leaf, its view state loads with Items.
import { workItemSearchQuery } from '@/stores/workItemSearchQuery';
import { setWorkItemSearchQuery } from '@/stores/workItemViewStoreSlot';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { useFittingPlaceholder } from '@/hooks/useFittingPlaceholder';
import { useT } from '@/i18n/LocaleProvider';
import { lazyOverlay } from '@/routing/lazyRoute';
import { ChunkFailBoundary } from '@/routing/ChunkFailBoundary';
import { isResultsPath } from './globalSearchTransition';
import { OPEN_SEARCH_BAR_EVENT } from './focusGlobalSearch';
import { SEARCH_CORRECTION_ID } from './searchStates';
import { takesText } from './takesText';
import { useViewSearchBar } from './viewSearchScopes';
import type { BarKeysType } from './BarResultsPanel';

// Its own chunk, fetched when the caret first comes into the bar: the launch entry carries the field.
const BarResultsPanel = lazyOverlay(
  () => import('./BarResultsPanel'),
  (module) => module.BarResultsPanel,
);

/**
 * The app's one search and command bar (F-0743 §4, D-0431), in the shell rather than on any view.
 *
 * What it searches is what is on screen (VSB-32): the view that registered its search, in that
 * view's own address; on Items, the work items' search. Elsewhere its text is its own, and its
 * results are the work area's items, the places to go and the commands (VSB-34). It never moves the
 * reader by itself: F-0176's switch to Items at the third character is gone, and **Show all in Work
 * items** goes there.
 *
 * ⚠️ **Where this is mounted is the feature.** It is rendered by `Layout` above the router's
 * `<Outlet/>`, so a route change underneath it does not touch it: the element the reader is typing
 * into is the same element before and after the switch, still focused, with the caret where they
 * left it. Nothing here restores focus afterwards, because nothing takes it away. Move this
 * component inside a routed view and the requirement is lost while every screenshot still looks
 * right — which is why its test asserts element identity rather than that a field exists.
 */
/**
 * VSB-51 (I-2419): the bar's own text as one placement leaves while the screen settles on its own
 * address. At 390 the old `/settings` is drawn with the top bar and `/-/settings` with the phone's, so
 * the shell swaps one bar for the other under the reader's keys; the arriving bar takes the text, and
 * the caret with its results if the leaving one had it. Only after a replace, and only at once.
 */
let settlingText: { text: string; focused: boolean; at: number } | null = null;
const SETTLING_HANDOVER_MS = 1_000;

export const GlobalSearchBar = observer(function GlobalSearchBar({
  placement = 'phone',
  guest = false,
}: {
  /** At the top of a large form factor ⌘K opens it (VSB-34); in a phone's list it is the list's. */
  placement?: 'top' | 'phone';
  /** PX-32 (I-2384): on a guest's work area, which has no commands: it says what it searches. */
  guest?: boolean;
}) {
  const t = useT();
  const location = useLocation();
  // T-0770 (LC-6c): the bar is in the launch entry, so it reads the item store only once a screen
  // has loaded it; before that no search has run, so nothing has been refused.
  const workItemStore = usePeekedWorkItemStore();
  /*
   * F-0743 §4 (D-0431, VSB-32): the view on screen that registered its search is the current
   * content, and the bar searches it, in that view's own address. On Items the bar is the work
   * items' search; anywhere else its text is its own, kept for this screen only (VSB-33).
   */
  const scope = useViewSearchBar()?.scopes.current ?? null;
  const onItems = isResultsPath(location.pathname);
  const [ownText, setOwnText] = useState('');
  const [openResults, setOpenResults] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const resultsKeys = useRef<BarKeysType | null>(null);
  const resultsId = useId();
  const navigationType = useNavigationType();
  useEffect(() => {
    /*
     * VSB-51 (I-2419): a replaced address is the screen settling on its own address, not another
     * screen (`/settings` becomes `/-/settings` after the first paint). What the reader typed
     * meanwhile stays, with its results open.
     */
    if (navigationType === NavigationType.Replace) return;
    setOwnText('');
    setOpenResults(false);
    // Only on a new address: the navigation's type is read with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  /*
   * The bar is on the route from its first paint, and a view registers its search a moment later
   * (its chunk, its effect). What the reader typed in between is handed to the view's search when
   * it registers, never replaced by its empty one.
   */
  const hasScope = scope !== null;
  useEffect(() => {
    if (scope && ownText && !scope.value) {
      scope.onChange(ownText);
      setOwnText('');
    }
    // Only as a view registers: what is typed after that is the view's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasScope]);

  const query = scope ? scope.value : onItems ? workItemSearchQuery.query : ownText;
  const setQuery = (next: string) => {
    if (scope) scope.onChange(next);
    else if (onItems) setWorkItemSearchQuery(next);
    else setOwnText(next);
  };
  const hasQuery = query.trim().length > 0;
  // A result was opened: the results close, and the bar's own text with them (a view's stays).
  const resultOpened = useCallback(() => {
    setOpenResults(false);
    setOwnText('');
  }, []);
  // I-1319: while the server refuses the search for good, the box is what to correct, and the
  // Items page's note says how (`SearchCorrection`).
  const refused =
    scope === null &&
    onItems &&
    hasQuery &&
    workItemStore !== undefined &&
    workItemStore.searchRefusedKey !== '' &&
    workItemStore.searchFailure !== null;
  /*
   * A refused search ran nothing, so the results would claim matches for text the server did not
   * search, and at 390 they cover the note that says how to correct it: they wait for the next
   * search (items-search-refused).
   */
  const resultsShown = openResults && !refused;

  const inputRef = useRef<HTMLInputElement>(null);
  // VSB-51: the leaving placement hands its text over (above); the arriving one takes it.
  const ownTextNow = useRef(ownText);
  ownTextNow.current = ownText;
  useLayoutEffect(() => {
    const input = inputRef.current;
    const handed = settlingText;
    settlingText = null;
    if (
      handed &&
      navigationType === NavigationType.Replace &&
      Date.now() - handed.at < SETTLING_HANDOVER_MS
    ) {
      setOwnText(handed.text);
      if (handed.focused) {
        input?.focus();
        setOpenResults(true);
      }
    }
    // Run before the field leaves the document, while it still says whether it has the caret.
    return () => {
      settlingText = ownTextNow.current
        ? { text: ownTextNow.current, focused: document.activeElement === input, at: Date.now() }
        : null;
    };
    // Once per placement: the text is read through its ref when it leaves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // What the search reads, in the one wording every search box and the CLI's help use (I-1300).
  const placeholder = useFittingPlaceholder(
    inputRef,
    onItems
      ? t('navigation.search.placeholder')
      : guest
        ? t('navigation.search.guestBarPlaceholder')
        : t('navigation.search.barPlaceholder'),
    t('navigation.search.short'),
  );

  // ⌘K at the top of a large form factor: the caret is put here, and the results open (VSB-34).
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return undefined;
    const open = () => {
      void BarResultsPanel.preload();
      setOpenResults(true);
    };
    input.addEventListener(OPEN_SEARCH_BAR_EVENT, open);
    return () => input.removeEventListener(OPEN_SEARCH_BAR_EVENT, open);
  }, []);

  // A view that moves the caret into its search from elsewhere (Agents) is handed the bar's input.
  const scopeInputRef = scope?.inputRef;
  useEffect(() => {
    if (!scopeInputRef) return undefined;
    const assign = (node: HTMLInputElement | null) => {
      if (typeof scopeInputRef === 'function') scopeInputRef(node);
      else (scopeInputRef as { current: HTMLInputElement | null }).current = node;
    };
    assign(inputRef.current);
    return () => assign(null);
  }, [scopeInputRef]);

  // `/` puts the caret in the bar (VSB-2, VSB-34), unless the caret is already where it types.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.defaultPrevented || takesText(event.target)) return;
      event.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    // I-1286: with a coarse pointer the field is a 44 px target, by the one rule (`index.css`).
    <div data-coarse-targets="" className="relative w-full sm:max-w-md lg:max-w-lg">
      <Search
        className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
        aria-hidden="true"
      />
      <input
        ref={inputRef}
        type="search"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpenResults(true);
        }}
        onKeyDown={(event) => {
          // VSB-34: the arrows move through the results and Enter opens one, the caret kept here.
          if (resultsShown && resultsKeys.current?.(event)) return;
          if (event.key === 'ArrowDown' && !openResults) {
            event.preventDefault();
            setOpenResults(true);
            return;
          }
          if (event.key === 'Escape') {
            // VSB-3, VSB-34: Escape empties the text and keeps the caret; over an empty one it is
            // the view's (a tree's, a panel's), and otherwise it leaves the bar.
            if (query) {
              event.preventDefault();
              event.stopPropagation();
              setQuery('');
              return;
            }
            scope?.onKeyDown?.(event);
            if (event.defaultPrevented) return;
            event.preventDefault();
            event.stopPropagation();
            setOpenResults(false);
            event.currentTarget.blur();
            return;
          }
          scope?.onKeyDown?.(event);
        }}
        /*
         * While the cursor is in here, this field owns the query and the URL follows it. During the
         * switch the address bar is one keystroke behind by construction, and without this the
         * arriving view's hydration would write that stale value back over the character the reader
         * had already typed. See `WorkItemViewStore.hydrateFromUrl`.
         */
        onFocus={() => {
          workItemSearchQuery.setOwnedByInput(true);
          void BarResultsPanel.preload();
        }}
        onBlur={() => {
          workItemSearchQuery.setOwnedByInput(false);
          setOpenResults(false);
        }}
        // A searchbox that controls its results (VSB-34): the view searches keep their role and
        // name, and the chosen result is read through `aria-activedescendant`.
        aria-controls={resultsShown ? resultsId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={resultsShown && activeId ? activeId : undefined}
        placeholder={scope ? (scope.placeholder ?? t('viewSearch.placeholder')) : placeholder}
        aria-label={
          scope
            ? (scope.label ?? t('viewSearch.label'))
            : onItems
              ? t('navigation.search.label')
              : t('navigation.search.bar')
        }
        aria-keyshortcuts="/"
        data-search-scope={scope ? 'view' : onItems ? 'items' : 'bar'}
        data-search-bar={placement}
        aria-invalid={refused || undefined}
        aria-describedby={refused ? SEARCH_CORRECTION_ID : undefined}
        // I-1300: what a search reads, and how to ask for a phrase, where a pointer rests.
        title={t('navigation.search.hint')}
        // How the shell finds the field (⌘F, `focusGlobalSearch`): by this mark, never by its
        // label, which is in the reader's language.
        data-global-search
        data-coarse-target=""
        // 16px below `sm` or focusing the field zooms the whole page on a phone.
        className={`block max-sm:min-h-touch w-full rounded-md border-0 bg-gray-100 pl-[calc(2rem)] ${
          // The clear button's room, only while there is something to clear (I-0876).
          // I-1321 (CFC-f4c): a finger's Clear search is 44 px, so its room is too.
          hasQuery ? 'pr-[calc(2rem)] max-sm:pr-11 [@media(pointer:coarse)]:pr-11' : 'pr-2.5'
        } text-base [&::-webkit-search-cancel-button]:hidden text-gray-900 transition-colors duration-150 hover:bg-gray-200 focus:bg-white focus:outline-none focus:ring-2 focus:ring-focus dark:bg-[#303030] dark:text-gray-100 dark:hover:bg-[#3a3a3a] dark:focus:bg-[#212121] sm:h-8 sm:text-[13px]`}
      />
      {hasQuery && (
        <button
          type="button"
          /*
           * I-0741: clearing is the field's own edit, so the field keeps owning the query through
           * it. Pressing the button took focus off the field, and during the switch the address is
           * a keystroke behind: the arriving view's hydration then wrote that old query (`featur`)
           * over the empty field before the return trip had seen it empty, and left the reader on
           * the results. The pointer no longer takes focus, and the field has it back either way.
           */
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            if (!scope && onItems) workItemSearchQuery.setOwnedByInput(true);
            setQuery('');
            inputRef.current?.focus();
          }}
          aria-label={t('navigation.search.clear')}
          title={t('navigation.search.clear')}
          // I-1321 (CFC-f4c): a 44 px target wherever a finger presses it (a phone, a coarse
          // pointer); its glyph stays 14 px. It was drawn 20 × 20 and a press 12 px from its centre
          // found the field.
          className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 cursor-pointer max-sm:right-0 max-sm:h-touch max-sm:w-touch [@media(pointer:coarse)]:right-0 [@media(pointer:coarse)]:h-touch [@media(pointer:coarse)]:w-touch items-center justify-center rounded text-gray-400 transition-colors duration-150 hover:bg-[#D7DBDC]/60 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus dark:hover:bg-[#242424] dark:hover:text-gray-100"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {resultsShown ? (
        <ChunkFailBoundary
          retry={BarResultsPanel.preload}
          // Offline the results' chunk does not load: they say so, and the field goes on working.
          failed={(retry) => (
            <div
              id={resultsId}
              role="alert"
              className="absolute inset-x-0 top-full z-50 mt-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[13px] shadow-lg dark:border-white/10 dark:bg-[#242424]"
              // The caret stays in the bar: pressing Try again must not take it and close this.
              onMouseDown={(event) => event.preventDefault()}
            >
              {t('navigation.search.resultsFailed')}{' '}
              <button type="button" onClick={retry} className="font-medium evx-link">
                {t('common.waiting.tryAgain')}
              </button>
            </div>
          )}
        >
          <BarResultsPanel
            id={resultsId}
            query={query}
            currentLabel={
              scope
                ? (scope.label ?? t('viewSearch.label'))
                : onItems
                  ? t('navigation.search.label')
                  : null
            }
            onItems={onItems}
            keys={resultsKeys}
            onActiveChange={setActiveId}
            onDone={resultOpened}
          />
        </ChunkFailBoundary>
      ) : null}
    </div>
  );
});
